import {
  ApplicationRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  PLATFORM_ID,
  PendingTasks,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NodePerson } from '@app/models/entity.model';
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { MediaComponent } from '@app/components/media/media.component';
import { ContentSectionComponent } from '@app/components/content-section/content-section.component';
import { TextComponent } from '@app/components/text/text.component';
import {
  MobiUiIconComponent,
  MobiUiOverlayComponents,
} from '@mobi/rwc-ui-components-ng-jslib';
import { TerminvereinbarungWidget } from '@mobi/dip-terminvereinbarung-jslib';
import { MapSmallComponent } from '@app/components/map-small/map-small.component';
import { Media } from '@app/models/media.model';
import { ContactCardComponent } from '@app/components/contact-card/contact-card.component';
import { IconLinkComponent } from '@app/components/person/icon-link/icon-link.component';
import { MiscItemComponent } from '@app/components/person/misc-item/misc-item.component';
import { DrupalService } from '@app/services/drupal.service';
import { DataService } from '@app/services/data.service';
import { FileDownloadComponent } from '@app/components/file-download/file-download.component';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { GoogleRatingComponent } from '@app/components/google-rating/google-rating.component';
import { MobiLdSchemaComponent } from '@app/components/mobi-ld-schema/mobi-ld-schema.component';
import { filter, take } from 'rxjs';
import { RokkaLoaderService } from '@app/services/rokka-loader.service';
import { environment } from '@src/environments/environment';

type PersonGaOnApQuery = {
  person?: {
    locationActivity?: Array<{
      location?: {
        generalAgency?: {
          onlineAppointment?: boolean;
        };
      };
    }>;
  };
};

@Component({
  selector: 'mobi-node-person',
  templateUrl: './node-person.component.html',
  styleUrls: ['./node-person.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    BreadcrumbComponent,
    MediaComponent,
    ContentSectionComponent,
    TextComponent,
    MobiUiIconComponent,
    MapSmallComponent,
    ContactCardComponent,
    IconLinkComponent,
    MiscItemComponent,
    FileDownloadComponent,
    GoogleRatingComponent,
    MobiLdSchemaComponent,
    MobiUiOverlayComponents,
    TerminvereinbarungWidget,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NodePersonComponent {
  CICERO_IMAGE_ID = '267';
  readonly entity = input.required<NodePerson>();
  #task = inject(PendingTasks);
  #drupal = inject(DrupalService);
  #data = inject(DataService);
  #appRef = inject(ApplicationRef);
  #platformId = inject(PLATFORM_ID);
  hasWap = computed(() => this.entity().gaRef?.gaWap);
  gaOnApButton = viewChild<ElementRef<HTMLButtonElement>>('gaOnApButton');
  #isBrowser = isPlatformBrowser(this.#platformId);
  #shouldOpenOnAp = computed(() =>
    ['1', 'true'].includes(this.#drupal.queryParams().get('onap') ?? '')
  );
  #onApOpened = false;
  #canAutoOpenOnAp = signal(false);
  #rokkaLoader = inject(RokkaLoaderService);

  ciceroImage = signal<Media | undefined>(undefined);

  video = computed<Media | undefined>(() => {
    const value = this.entity()?.video?.value;
    if (!value) return;

    return {
      type: 'MediaRemoteVideo',
      embedUrl: value,
      uuid: 'video-' + this.entity().uuid,
    } as Media;
  });

  hasLocation = computed(() => {
    return this.entity()?.locationActivity && this.entity()?.locationActivity.length;
  });

  mainLocation = computed(() => {
    const gaLocation = this.entity()?.locationActivity?.find(
      (a) => a.location.mainAgency
    )?.location;

    return gaLocation;
  });

  gaOnAp = computed(
    () =>
      this.entity()?.locationActivity?.some(
        (a) => a.location?.generalAgency?.onlineAppointment
      ) ?? false
  );

  onapLanguage = computed(() => this.#drupal.language() as 'de' | 'fr' | 'it');

  /** OVT (Online Terminvereinbarung) expects the worker email on a single domain in every language,
   * while the CMS delivers language-specific domains (mobiliar.ch / mobiliere.ch / mobiliare.ch).
   * The target domain is environment-specific: mobi.ch on prod and preprod,
   * mobiliar-test.ch on the test environments. */
  onapWorkerEmail = computed(() => {
    const localPart = this.entity().email?.trim().split('@')[0];
    return localPart ? `${localPart}@${environment.terminvereinbarungEmailDomain}` : '';
  });

  whatsAppPhone = computed(() => {
    return this.mainLocation()?.phone ?? '';
  });

  whatsAppLink = computed(() => {
    const phone = this.whatsAppPhone();
    if (!phone) return '';
    const digits = phone.replace(/\s/g, '');
    return `https://wa.me/41${digits.startsWith('0') ? digits.slice(1) : digits}`;
  });

  rokkaAvatar = computed(() => {
    return `dynamic/noop/${this.entity().image?.image?.entity?.rokka?.hash}/${this.entity().image?.image?.entity?.filename}`;
  });

  #certificationTexts: { [key: string]: string } = {
    de: 'Cicero, Berufsbildungsverband der Versicherungswirtschaft VBV',
    fr: 'Cicero, Association pour la formation professionnelle en assurance AFA ',
    it: "Cicero, Associazione per la formazione professionale nell'assicurazione AFA",
  };

  personSchema = computed(() => {
    const entity = this.entity();
    return {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: {
        '@type': 'Person',
        email: entity.email,
        telephone: entity.phone,
        jobTitle: entity.functionText || entity.functionTaxonomy?.label || '',
        name: `${entity.firstName} ${entity.lastName}`,
        ...(entity.ciceroNr
          ? {
              hasCertification: [
                {
                  '@type': 'Certification',
                  certificationIdentification: `Cicero Member Nr. ${entity.ciceroNr}`,
                  issuedBy: this.#certificationTexts[this.#drupal.language()],
                },
              ],
            }
          : {}),
        knowsLanguage: entity.languages,
        memberOf: [
          {
            '@type': 'Organization',
            name: this.#data.orgNames[this.#drupal.language()],
          },
        ],
        worksFor: [
          {
            '@type': 'Organization',
            name: this.#data.orgNames[this.#drupal.language()],
          },
        ],
        ...(entity.career ? { knowsAbout: entity.career } : {}),
        ...(entity.education ? { skills: entity.education } : {}),
      },
    };
  });

  // SHCWS-2096: Leitung (129) and Generalagent (133) are not bookable, only advisors are.
  readonly ALLOWED_ACTIVITIES = [
    '117', // Beratung
  ];

  // Any location counts, not only the main agency: advisors at branch offices need OVT too.
  isActivityAllowed = computed(
    () =>
      this.entity()?.locationActivity?.some(
        (a) =>
          a.location?.generalAgency?.onlineAppointment &&
          a.activities?.some((activity) => this.ALLOWED_ACTIVITIES.includes(activity.id))
      ) ?? false
  );

  constructor() {
    if (this.#isBrowser && this.#shouldOpenOnAp()) {
      this.#appRef.isStable.pipe(filter(Boolean), take(1)).subscribe(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#canAutoOpenOnAp.set(true);
          });
        });
      });
    }

    effect(() => {
      if (!this.#canAutoOpenOnAp() || this.#onApOpened) return;
      const button = this.gaOnApButton()?.nativeElement;
      if (!button) return;
      this.#onApOpened = true;
      button.click();
    });

    const cleanup = this.#task.add();
    this.#data
      .loadMedia(this.CICERO_IMAGE_ID)
      .then((media) => {
        // set ciceroImage to media
        this.ciceroImage.set(media);
        cleanup();
      })
      .catch((e) => {
        console.log(e);
        cleanup();
      });
  }
}
